import React, { useEffect, useState } from 'react';
import { BellIcon as BellOutline } from '@heroicons/react/24/outline';
import { BellIcon as BellSolid } from '@heroicons/react/24/solid';
import apiService from '../services/ApiService';
import type { AnalyticsEventName } from '../services/api/analytics';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';

interface BuildWatchButtonProps {
  vehicleId: number;
  /** Watching requires a favorite; the button renders nothing otherwise. */
  isFavorited: boolean;
  className?: string;
}

/**
 * Toggle build-update notifications for a favorited build.
 * Renders nothing when signed out or when the build is not favorited.
 */
export const BuildWatchButton: React.FC<BuildWatchButtonProps> = ({
  vehicleId,
  isFavorited,
  className = '',
}) => {
  const { isAuthenticated } = useAuth();
  const { showToast } = useToast();
  const [watching, setWatching] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [pending, setPending] = useState(false);

  const eligible = isAuthenticated && isFavorited;

  useEffect(() => {
    if (!eligible) {
      setLoaded(false);
      setWatching(false);
      return undefined;
    }
    let cancelled = false;
    setLoaded(false);
    (async () => {
      try {
        const status = await apiService.getWatchStatus(vehicleId);
        if (!cancelled) setWatching(status.watching);
      } catch {
        // Fall back to "not watching"; toggling will surface real errors.
        if (!cancelled) setWatching(false);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [eligible, vehicleId]);

  if (!eligible) return null;

  const toggle = async () => {
    if (pending || !loaded) return;
    const previous = watching;
    const next = !watching;
    setWatching(next);
    setPending(true);
    try {
      const confirmed = await apiService.setWatchStatus(vehicleId, next);
      setWatching(confirmed);
      showToast(
        confirmed ? 'You will get updates about this build.' : 'You will no longer get updates about this build.',
        'success'
      );
      void apiService.trackEvent(
        (next ? 'build_watch_enabled' : 'build_watch_disabled') as AnalyticsEventName,
        { vehicle_id: vehicleId }
      );
    } catch (e: unknown) {
      setWatching(previous);
      showToast(e instanceof Error && e.message ? e.message : 'Could not update watch status. Try again.', 'error');
    } finally {
      setPending(false);
    }
  };

  const label = watching ? 'Stop watching' : 'Watch updates';

  return (
    <button
      type="button"
      onClick={() => void toggle()}
      disabled={pending || !loaded}
      aria-pressed={watching}
      aria-label={label}
      title={label}
      className={[
        'inline-flex items-center justify-center gap-1.5 min-h-[44px] px-3 rounded-lg border text-sm font-medium',
        'transition-colors select-none touch-manipulation',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/60 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-900',
        watching
          ? 'border-orange-500/40 bg-orange-500/10 text-orange-200 hover:bg-orange-500/15'
          : 'border-white/[0.10] bg-[#141414]/70 text-gray-200 hover:bg-white/[0.06] hover:text-white',
        pending || !loaded ? 'opacity-70 cursor-wait' : 'cursor-pointer',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {!loaded ? (
        <>
          <span className="spinner-sm" aria-hidden />
          <span className="sr-only">Checking watch status…</span>
        </>
      ) : watching ? (
        <BellSolid className="w-5 h-5 text-orange-400" aria-hidden />
      ) : (
        <BellOutline className="w-5 h-5" aria-hidden />
      )}
      {loaded && <span>{label}</span>}
    </button>
  );
};

export default BuildWatchButton;
